import { SortDirection } from '../../components/table/sortable-header';
import { positiveInteger } from '../../lib/url/hooks/use-queries-control';
import { CompanyItemsAPIParams, ItemCategoriesAPIData } from '../../queries/rewards';
import { QueryState } from './hooks';
import { abstractCategoryKeys, CategoryKey, categoryKeys } from './types';

export const getUiHelpers = (data: ItemCategoriesAPIData) => {
	const { curatedItems, exclusiveItems, featuredItems, swagItems } = data.meta;
	const showCollections = [curatedItems, exclusiveItems, featuredItems, swagItems].some(Boolean);

	return {
		showCollections,
		showCuratedItems: curatedItems,
		showExclusiveItems: exclusiveItems,
		showFeaturedItems: featuredItems,
		showSwagItems: swagItems,
	};
};

export const getDefaultCategoryKey = (data: ItemCategoriesAPIData) => {
	const ui = getUiHelpers(data);

	if (ui.showFeaturedItems) {
		return 'featured';
	}

	if (ui.showCuratedItems) {
		return 'curated';
	}

	if (ui.showExclusiveItems) {
		return 'exclusive';
	}

	if (ui.showSwagItems) {
		return 'swag';
	}

	return 'all';
};

// This converts the URL parameters into an object that can be passed into the API handler.
// There are a few conversions and logical checks we need to do. Ex. a category of "shopping" needs
// to be converted to the ID of the category, while other selected options like "swag" are actually
// filters on the option type.
export const formatIndexParams = (queries: QueryState, categories: ItemCategoriesAPIData): CompanyItemsAPIParams => {
	const params: CompanyItemsAPIParams = {
		pageNumber: queries.pageNumber,
	};

	if (queries.category === 'swag' || queries.category === 'exclusive') {
		params.itemType = queries.category;
	}

	if (queries.category === 'curated' || queries.focusCurated) {
		params.focusCurated = true;
	}

	if (queries.subCategory) {
		params.subCategory = queries.subCategory;
	}

	if (!abstractCategoryKeys.includes(queries.category as any)) {
		const category = categories.data.find((c) => c.key === queries.category);

		if (category) {
			params.category = category.id;
		}
	}

	return params;
};

type CreateQueryParamParserConfig = {
	defaultCategoryKey: CategoryKey;
	subCategories: string[];
};

export const createIndexParamParser = ({ defaultCategoryKey, subCategories }: CreateQueryParamParserConfig) => {
	const params = ['category', 'subCategory', 'focusCurated', 'pageNumber', 'dialog', 'rewardId'] as const;
	const config = {
		allowedValues: {
			category: categoryKeys,
			subCategory: subCategories,
			focusCurated: ['true', 'false'],
		},
		defaultValue: {
			category: defaultCategoryKey,
			focusCurated: 'false',
			pageNumber: '1',
		},
		transforms: {
			focusCurated: (value: string) => value === 'true',
			pageNumber: positiveInteger,
		},
	};

	return [params, config] as const;
};

export const createHistoryDetailParamParser = () => {
	return [
		['page', 'sortBy', 'sortDirection', 'dialog', 'rewardId'],
		{
			defaultValue: {
				page: '1',
				sortBy: 'created_at',
				sortDirection: SortDirection.DESC,
			},
			allowedValues: {
				sortBy: ['created_at'],
				sortDirection: [SortDirection.ASC, SortDirection.DESC],
			},
			transforms: {
				page: positiveInteger,
			},
		},
	] as const;
};
